import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { RecommendedImage } from '@bingads-webui-campaign-react/display-recommended-image';
import { UploadImage } from '@bingads-webui-campaign-react/display-ads-editor';
import { useImagePicker } from '@bingads-webui-campaign-react/add-image-hooks';
import { mergeClasses, Divider } from '@fluentui/react-components';
import { Stack } from '@fluentui/react/lib/Stack';
import { useStyles } from './style';
import { SceneWrapperTextAssetCollectionWithRecommendations } from '../../components';

export const EditVideoAssetsView = ({
  i18n,
  aid,
  cid,
  oDataConfig,
  onTextListChange,
  onChangeImage,
  scenes,
  selectedSceneId,
  onImageUpdated,
  onLogoUpdated,
  onChangeLogo,
  preferenceService,
  serverContext,
  perfMarker,
  permissions,
  recommendedAssets,
  isLoadingRecommendedAssets,
  brandKit,
  isLoadingBrandKit,
  finalUrl,
}) => {
  const classes = useStyles();
  const [textList, setTextList] = useState();
  const [assetType, setAssetType] = useState('');
  const [assetIndex, setAssetIndex] = useState('');
  const {
    displayImagePicker,
    setDisplayImagePicker,
    selectedMediaPanelSource,
    setSelectedMediaPanelSource,
  } = useImagePicker();

  const currentScene = useMemo(() => scenes[selectedSceneId], [scenes, selectedSceneId]);

  const metaData = useMemo(() => {
    if (currentScene && currentScene.updating) {
      return ({ ...currentScene.updating, newImages: currentScene.newImages });
    }
    return null;
  }, [currentScene]);

  const currentSceneImages = useMemo(() => ((currentScene && currentScene.newImages && currentScene.newImages.filter(image => image.url)) || []), [currentScene]);
  const currentSceneLogos = useMemo(() => ((currentScene && currentScene.newLogos && currentScene.newLogos.filter(logo => logo.url)) || []), [currentScene]);
  const hasImages = useMemo(() => currentSceneImages.length, [currentSceneImages]);
  const hasLogos = useMemo(() => currentSceneLogos.length, [currentSceneLogos]);
  const brandKitImages = useMemo(() => {
    if (brandKit && brandKit.images) {
      const assets = Array.from(brandKit.images);
      return assets.map(image => ({ ...image, fullUrl: image.Url, thumbnailSrc: image.Url }));
    }
    return [];
  }, [brandKit]);

  const recommendedImageList = useMemo(() => {
    if (recommendedAssets && recommendedAssets.ImageAssets) {
      const assets = Array.from(recommendedAssets.ImageAssets);
      const mappedAssets = assets.map(image => ({ ...image, fullUrl: image.Url, thumbnailSrc: image.Url }));
      return mappedAssets.concat(brandKitImages);
    }
    return [];
  }, [recommendedAssets, brandKitImages]);

  const recommendedLogoList = useMemo(() => {
    let mappedLogos = [];
    if (brandKit && brandKit.logos) {
      const assets = Array.from(brandKit.logos);
      mappedLogos = mappedLogos.concat(assets.map(logo => ({ ...logo, fullUrl: logo.Url, thumbnailSrc: logo.Url })));
    }
    if (recommendedAssets && recommendedAssets.Logo) {
      const assets = Array.isArray(recommendedAssets.Logo) ? recommendedAssets.Logo : [recommendedAssets.Logo];
      mappedLogos = mappedLogos.concat(assets.map(logo => ({ fullUrl: logo, thumbnailSrc: logo })));
    }
    return mappedLogos;
  }, [brandKit, recommendedAssets]);

  const onReplacePreview = useCallback((index, ev) => {
    const { type } = ev.currentTarget.dataset;
    setDisplayImagePicker(true);
    setAssetType(type);
    setAssetIndex(index);
  }, [setDisplayImagePicker]);

  const onEdit = useCallback(() => {
    onChangeLogo();
  }, [onChangeLogo]);

  const onTextListUpdate = useCallback((newText, textData, index) => {
    if (!newText || !newText.shortHeadline) {
      return;
    }

    let maxLength = 0;
    if (textData && textData.length > index) {
      // eslint-disable-next-line prefer-destructuring
      maxLength = textData[index].maxLength;
    }

    const str = maxLength > 0 ? newText.shortHeadline.slice(0, maxLength) : newText.shortHeadline;
    const newTextList = [...textList];
    newTextList[index] = str;
    setTextList(newTextList);
    onTextListChange(newTextList);
  }, [onTextListChange, textList]);

  const onUpdateImageFromUserAssets = index => (image, imageType) => {
    // Asset picker always returns array, but we limit it to 1
    const imageSrc = (image.length && image[0]) || {};
    if (imageType === 'image') {
      const newImageArray = [...currentSceneImages];
      // update image, and set url as standard reference over src
      newImageArray[index] = { ...imageSrc, url: (imageSrc.src || imageSrc.fullUrl || imageSrc.thumbnailSrc) };
      onImageUpdated(newImageArray);
    } else if (imageType === 'logo') {
      const newLogoArray = [...currentSceneLogos];
      newLogoArray[index] = { ...imageSrc, url: (imageSrc.src || imageSrc.fullUrl || imageSrc.thumbnailSrc) };
      onLogoUpdated(newLogoArray);
    }
  };

  const onUpdateImageFromRecommendations = index => (image, imageType) => {
    if (imageType === 'image') {
      const newImageArray = [...currentSceneImages];
      newImageArray[index] = { url: (image.src || image.fullUrl || image.thumbnailSrc) };
      onImageUpdated(newImageArray);
    } else if (imageType === 'logo') {
      const newLogoArray = [...currentSceneLogos];
      newLogoArray[index] = { url: (image.src || image.fullUrl || image.thumbnailSrc) };
      onLogoUpdated(newLogoArray);
    }
  };

  useEffect(() => {
    if (metaData && metaData.text && metaData.images) {
      setTextList(metaData.text);
    }
  }, [metaData]);

  return (
    <div className="edit-video-assets-view">
      <div className="edit-video-assets-title">
        <div className={mergeClasses('edit-video-assets-scene-navigation', classes.topNavigation)} >
          <Stack>
            {hasLogos && (
              <>
                <Stack.Item>
                  <div className="edit-logo-container">
                    {currentSceneLogos.map((logo, index) => (
                      <div className={mergeClasses(classes.recommendedImage, 'recommended-image')}>
                        <RecommendedImage
                          key={logo.url || index}
                          i18n={i18n}
                          isLoading={isLoadingBrandKit}
                          previewImage={logo.url}
                          recommendedImages={recommendedLogoList}
                          maxUploadNum={1}
                          uiBlocking={false}
                          type="logo"
                          seeAllPanelTitle={i18n.getString(_TL_('Recommended logos'))}
                          seeAllLinkText={i18n.getString(_TL_('View all'))}
                          onEditPreviewImage={onEdit}
                          onReplacePreviewImage={ev => onReplacePreview(index, ev)}
                          recommendedLoadingText={i18n.getString(_TL_('Loading'))}
                          title={i18n.getString(_TL_('Logo'))}
                          helpTopic="pop_BA_VideoTemplates_Logo"
                          helpLabel="Learn more about logos"
                          recommendationTitle={i18n.getString(_TL_('Recommended logos'))}
                          onSelectRecommendedImage={onUpdateImageFromRecommendations(index)}
                          onAddRecommendedImagefromPanel={onUpdateImageFromRecommendations(index)}
                          showViewAllInHeader
                          hideEditPreviewBtn
                        />
                      </div>
                ))
              }
                  </div>
                </Stack.Item>
                <div className={classes.divider}>
                  <Divider />
                </div>
              </>
            )}
            {
              hasImages && (
                <>
                  <Stack.Item>
                    <div className="edit-image-container">
                      {currentSceneImages.map((sceneImage, index) => (
                        <div className={mergeClasses(classes.recommendedImage, 'recommended-image')}>
                          <RecommendedImage
                            key={sceneImage.url || index}
                            i18n={i18n}
                            isLoading={isLoadingRecommendedAssets}
                            previewImage={sceneImage.url}
                            recommendedImages={recommendedImageList}
                            maxUploadNum={1}
                            uiBlocking={false}
                            type="image"
                            seeAllPanelTitle={i18n.getString(_TL_('Recommended images'))}
                            seeAllLinkText={i18n.getString(_TL_('View all'))}
                            onEditPreviewImage={onChangeImage}
                            onReplacePreviewImage={ev => onReplacePreview(index, ev)}
                            recommendedLoadingText={i18n.getString(_TL_('Loading'))}
                            title={i18n.getString(_TL_('Images'))}
                            helpTopic="pop_BA_VideoTemplates_Images"
                            helpLabel="Learn more about images"
                            recommendationTitle={i18n.getString(_TL_('Recommended images'))}
                            onSelectRecommendedImage={onUpdateImageFromRecommendations(index)}
                            onAddRecommendedImagefromPanel={onUpdateImageFromRecommendations(index)}
                            showViewAllInHeader
                            hideEditPreviewBtn
                          />
                        </div>
                            ))
                          }
                    </div>
                  </Stack.Item>
                  <div classeName={classes.divider}>
                    <Divider />
                  </div>
                </>
            )}
            <Stack.Item>
              <div className="text-container">
                {
                 scenes && scenes.map((scene, idx) => (
                   <SceneWrapperTextAssetCollectionWithRecommendations
                     key={scene.id}
                     scene={scene}
                     shouldDisplay={selectedSceneId === idx}
                     url={finalUrl}
                     disableCopilot={!finalUrl}
                     i18n={i18n}
                     campaignId={cid}
                     serverContext={serverContext}
                     onTextListUpdate={onTextListUpdate}
                   />
                  ))
                }
              </div>
            </Stack.Item>
          </Stack>
        </div>
      </div>
      {displayImagePicker && (
        <UploadImage
          displayImagePicker={displayImagePicker}
          setDisplayImagePicker={setDisplayImagePicker}
          selectedMediaPanelSource={selectedMediaPanelSource}
          setSelectedMediaPanelSource={setSelectedMediaPanelSource}
          elementType={assetType}
          i18n={i18n}
          maxLimit={1}
          preferenceService={preferenceService}
          serverContext={serverContext}
          aid={aid}
          cid={cid}
          onSelectImages={onUpdateImageFromUserAssets(assetIndex)}
          oDataConfig={oDataConfig}
          perfMarker={perfMarker}
          permissions={permissions}
        />
      )}
    </div>
  );
};

EditVideoAssetsView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  aid: PropTypes.string.isRequired,
  cid: PropTypes.string.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  assetLibraryConfig: PropTypes.shape({}).isRequired,
  onTextListChange: PropTypes.func.isRequired,
  onChangeImage: PropTypes.func.isRequired,
  onImageUpdated: PropTypes.func.isRequired,
  onLogoUpdated: PropTypes.func.isRequired,
  scenes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedSceneId: PropTypes.number.isRequired,
  onChangeLogo: PropTypes.func.isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  recommendedAssets: PropTypes.shape({
    ImageAssets: PropTypes.arrayOf(PropTypes.shape({
      Url: PropTypes.string,
    })),
    Logo: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }).isRequired,
  isLoadingRecommendedAssets: PropTypes.bool.isRequired,
  brandKit: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.shape({
      Url: PropTypes.string,
    })),
    logos: PropTypes.arrayOf(PropTypes.shape({
      Url: PropTypes.string,
    })),
  }).isRequired,
  isLoadingBrandKit: PropTypes.bool.isRequired,
  finalUrl: PropTypes.string,
};

EditVideoAssetsView.defaultProps = {
  finalUrl: '',
};
